import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import axios from "axios";

import ProtectedRoute from "./common/components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProProvider } from "./components/Optician/context/ProContext";
import PricingPlans from "./components/Optician/PricingPlans";
import PaymentForm from "./components/Optician/PaymentForm";
import SponsorList from "./components/Optician/Sponsors/Sponsors";
import AddSponsor from "./components/Optician/Sponsors/AddSponsors";
import Profile from "./components/Optician/Profil/Profil";
import Support from "./features/support/pages/Support";
import MyCustomers from "./components/MyCustomers/MyCustomers";
import Vouchers from "./features/vouchers/pages/Vouchers";
import {
  ForgotPassword,
  Login,
  Register,
  RegisterUser,
  ResetPassword,
} from "./features/auth/pages";
import ForceChangePassword from "./features/auth/pages/ForceChangePassword";
import QRCode from "./features/qr-code/pages/QRCode";
import UseMobileApp from "./features/redirect-user/pages/UseMobileApp";
import NewIban from "./components/Optician/NewIban";
import MyInvoices from "./features/invoices/pages/MyInvoices";
import Worker from "./features/worker/pages/Worker";
import { Activation } from "./features/activation";
import { PaymentLink } from "./features/payment-link";
import { Personalization } from "./features/personalization";
import api from "./services/api";

// Pro dashboard pages
import DashboardPage from "./components/Optician/pages/DashboardPage";
import OngoingPage from "./components/Optician/pages/OngoingPage";
import CompletedPage from "./components/Optician/pages/CompletedPage";
import SponsorsPage from "./components/Optician/pages/SponsorsPage";
import SettingsPage from "./components/Optician/pages/SettingsPage";
import SupportPage from "./components/Optician/pages/SupportPage";
import InvoicesPage from "./components/Optician/pages/InvoicesPage";
import VouchersPage from "./components/Optician/pages/VouchersPage";
import InvitePage from "./components/Optician/pages/InvitePage";
import NotifyPage from "./components/Optician/pages/NotifyPage";
import SmsPage from "./components/Optician/pages/SmsPage";
import GiftCardPage from "./components/Optician/pages/GiftCardPage";
import CardAdvantagesPage from "./components/Optician/pages/CardAdvantagesPage";
import PublishPage from "./components/Optician/pages/PublishPage";
import OrderNotifyPage from "./components/Optician/pages/OrderNotifyPage";
import RdvPage from "./components/Optician/pages/RdvPage";

function App() {
  const [qrCodeRoutes, setQrCodeRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const stripePromise = loadStripe(
    process.env.REACT_APP_ENV === "production"
      ? "pk_live_51PLuQiHzL8gubNCfOBaaohZ0Vwz2Rti3HVD5YSdxui9saiRuPA1T0XRfNKEfvmlxSeKBHJX3uGBcOGdd769RoChF00t2XrnVLW"
      : "pk_test_51PLuQiHzL8gubNCf7IJkAafxB2pidgeYPPcod5oKvoLddSxsZb2hkRHN68zUuRbf97rPmWHH31ir0IUHC27db5Yc00BxBPLFJB"
  );

  useEffect(() => {
    const fetchQRCodes = async () => {
      try {
        const response = await api.get("/qr-codes");
        setQrCodeRoutes(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des QR codes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQRCodes();
  }, []);

  console.log(qrCodeRoutes);

  return (
    <AuthProvider>
      <Elements stripe={stripePromise}>
        <Router>
          <div className="App h-screen flex flex-col">
            <div className="flex-1 flex flex-col min-h-0">
              {isLoading ? (
                <div>Chargement...</div>
              ) : (
                <Routes>
                  <Route path="/" element={<DefaultRedirect />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/sign-up" element={<RegisterUser />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/activation/:token" element={<Activation />} />
                  <Route path="/paiement/:token" element={<PaymentLink />} />
                  <Route path="/personalization/:token" element={<Personalization />} />
                  <Route 
                    path="/force-change-password" 
                    element={
                      <ProtectedRoute element={<ForceChangePassword />} />
                    } 
                  />
                  {qrCodeRoutes.map((qrCode) => (
                    <Route
                      key={qrCode.id}
                      path={qrCode.url_path}
                      element={<QRCode creatorId={qrCode.optician_id} />}
                    />
                  ))}
                  <Route path="/app-mobile" element={<UseMobileApp />} />
                  <Route
                    path="/optician-dashboard"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <ProProvider><DashboardPage /></ProProvider>
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route path="/optician/ongoing" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><OngoingPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/completed" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><CompletedPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/sponsors" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><SponsorsPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/settings" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><SettingsPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/support" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><SupportPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/invoices" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><InvoicesPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/vouchers" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><VouchersPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/invite" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><InvitePage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/notify" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><NotifyPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/sms" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><SmsPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/gift-card" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><GiftCardPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/card" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><CardAdvantagesPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/publish" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><PublishPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/order-notify" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><OrderNotifyPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route path="/optician/rdv" element={<ProtectedRoute element={<RequireOpticianRole><ProProvider><RdvPage /></ProProvider></RequireOpticianRole>} />} />
                  <Route
                    path="/customers"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <MyCustomers />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <MyInvoices />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/plan"
                    element={<ProtectedRoute element={<PricingPlans />} />}
                  />
                  <Route
                    path="/payment"
                    element={<ProtectedRoute element={<PaymentForm />} />}
                  />
                  <Route
                    path="/sponsors"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <SponsorList />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/workers"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <Worker />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/bons"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <Vouchers />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/profil"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <Profile />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/support"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOptician>
                            <Support />
                          </RequireOptician>
                        }
                      />
                    }
                  />
                  <Route
                    path="/optician-support"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <Support />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/sponsors/add"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <AddSponsor />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route
                    path="/change-card"
                    element={
                      <ProtectedRoute
                        element={
                          <RequireOpticianRole>
                            <NewIban />
                          </RequireOpticianRole>
                        }
                      />
                    }
                  />
                  <Route path="/default" element={<DefaultRedirect />} />
                  <Route
                    path="*"
                    element={<QRCodeOrDefault qrCodeRoutes={qrCodeRoutes} />}
                  />
                </Routes>
              )}
            </div>
          </div>
        </Router>
      </Elements>
    </AuthProvider>
  );
}

const DefaultRedirect = ({ children }) => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (currentUser.role === "optician") {
    return <Navigate to="/optician-dashboard" />;
  }

  if (currentUser.role !== "optician") {
    return <Navigate to="/app-mobile" />;
  }

  return children;
};
const RequireOpticianRole = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (currentUser.role !== "optician") {
    return <Navigate to="/default" />;
  }

  console.log(currentUser);

  if (
    currentUser.plan_level !== 1 &&
    (!currentUser.stripe_subscription_id || !currentUser.stripe_customer_id)
  ) {
    return <Navigate to="/plan" />;
  }

  return children;
};

const RequireOptician = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (currentUser.role !== "user") {
    return <Navigate to="/default" />;
  }

  return children;
};

// Nouveau composant pour traiter les URLs de QR code dynamiquement
const QRCodeOrDefault = ({ qrCodeRoutes }) => {
  const location = window.location.pathname;

  // Vérifier si l'URL actuelle correspond à un QR code connu
  const matchingQRCode = qrCodeRoutes.find((qr) => qr.url_path === location);

  if (matchingQRCode) {
    return <QRCode creatorId={matchingQRCode.optician_id} />;
  }

  // Si aucune correspondance, rediriger vers default
  return <Navigate to="/default" replace />;
};

export default App;
